import React from 'react';
import {useParams} from "react-router-dom";
import Box from "@mui/joy/Box";
import Table from "@mui/joy/Table";
import Typography from "@mui/joy/Typography";
import Stack from "@mui/joy/Stack";
import Button from "@mui/joy/Button";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { formatDateTime } from "../../../common/dateTime";
import {getPages} from "../../../common/pages/pages";
import {NotificationService} from "../../../common/notification";
import {userOperationsApi, getWebhookDataApi, manualHandlePaymentApi} from "../usersApi";
import {UserOperationInfo, UserOperationStatus, UserOperationType} from "../../../models/userOperation";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Input from "@mui/joy/Input";
import {Game} from "../../../models/games";
import {gamesListApi} from "../../games/gamesApi";
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import Sheet from "@mui/joy/Sheet";
import IconButton from "@mui/joy/IconButton";
import Tooltip from "@mui/joy/Tooltip";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Divider from "@mui/joy/Divider";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DebounceInput from '../../../components/debounceInput/DebounceInput';
import {formatRubles} from "../../../common/money";
import {PaymentOperationType} from "../../../models/paymentMethod";
import Link from '@mui/joy/Link';
import {formatJsonData} from "../../../common/utils";
import Chip from "@mui/joy/Chip";
import {PurchaseSource} from "../../../models/externalMarkets";

export function UserOperations() {
    const {id} = useParams();
    const [operations, setOperations] = React.useState<UserOperationInfo[]>([]);
    const [page, setPage] = React.useState(1);
    const [pageCount, setPageCount] = React.useState(1);
    const [filterTypes, setFilterTypes] = React.useState<number[]>([]);
    const [filterStatuses, setFilterStatuses] = React.useState<number[]>([]);
    const [dateFrom, setDateFrom] = React.useState<string | undefined>(undefined);
    const [dateTo, setDateTo] = React.useState<string | undefined>(undefined);
    const [games, setGames] = React.useState<Game[]>([]);
    const [loadedGames, setLoadedGames] = React.useState(false);
    const [selectedGame, setSelectedGame] = React.useState<number>(-1);
    const [search, setSearch] = React.useState<string>("");
    const [filterOpen, setFilterOpen] = React.useState(false);
    const [webhookModalOpen, setWebhookModalOpen] = React.useState(false);
    const [webhookData, setWebhookData] = React.useState<string | null>(null);
    const [webhookLoading, setWebhookLoading] = React.useState(false);
    const [selectedOperation, setSelectedOperation] = React.useState<UserOperationInfo | null>(null);
    const [manualModalOpen, setManualModalOpen] = React.useState(false);
    const [manualPaymentId, setManualPaymentId] = React.useState<string>('');
    const [manualOperation, setManualOperation] = React.useState<UserOperationInfo | null>(null);
    const [manualLoading, setManualLoading] = React.useState(false);

    React.useEffect(() => {
        if (id && BigInt(id) > 0) {
            loadData(BigInt(id)).catch(console.error);
        }
    }, [page])

    React.useEffect(() => {
        loadGames().catch(console.error);
    }, [])

    const loadGames = async () => {
        if (loadedGames) return;
        try {
            let result = await gamesListApi();
            if (result.result) {
                setGames(result.value!);
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoadedGames(true);
        }
    }

    const loadData = async (userId: bigint) => {
        try {
            let result = await userOperationsApi(userId, page, 20, {
                types: filterTypes.length ? (filterTypes as UserOperationType[]) : undefined,
                statuses: filterStatuses.length ? (filterStatuses as UserOperationStatus[]) : undefined,
                range: (dateFrom || dateTo) ? { min: dateFrom ? new Date(dateFrom).toISOString() : undefined, max: dateTo ? new Date(dateTo).toISOString() : undefined } : undefined,
                gameId: selectedGame > -1 ? selectedGame : undefined,
                search: search || undefined,
            });
            if (result.result) {
                const res = result.value! as any;
                const totalItems = res.total || 0;
                setPageCount(Math.max(1, Math.ceil(totalItems / 20)));
                setOperations(res.items)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
        }
    }

    const applyFilters = () => {
        if (page !== 1) {
            setPage(1);
            return;
        }
        if (id && BigInt(id) > 0) {
            loadData(BigInt(id)).catch(console.error);
        }
    }

    const typeName = (t: UserOperationType) => {
        switch (t) {
            case UserOperationType.Purchase: return 'Покупка';
            case UserOperationType.Sale: return 'Продажа';
            case UserOperationType.Withdrawal: return 'Вывод';
            case UserOperationType.Replenishment: return 'Пополнение';
            case UserOperationType.PromoBonus: return 'Бонус по промокоду';
            default: return `${t}`;
        }
    }

    const statusName = (s: UserOperationStatus) => {
        switch (s) {
            case UserOperationStatus.Success: return 'Успешно';
            case UserOperationStatus.Failure: return 'Не успешно';
            case UserOperationStatus.PenaltyPoint: return 'Штрафной балл';
            case UserOperationStatus.Refund: return 'Возврат';
            case UserOperationStatus.Waiting: return 'Ожидание';
            case UserOperationStatus.Processing: return 'Обработка';
            case UserOperationStatus.System: return 'Система';
            default: return `${s}`;
        }
    }

    const statusColors = (s: UserOperationStatus) => {
        // green: Success, Refund, System; red: Failure, PenaltyPoint; yellow: Processing, Waiting
        switch (s) {
            case UserOperationStatus.Success:
            case UserOperationStatus.Refund:
            case UserOperationStatus.System:
                return { bg: 'var(--joy-palette-success-softBg)', color: 'var(--joy-palette-success-800)' };
            case UserOperationStatus.Failure:
            case UserOperationStatus.PenaltyPoint:
                return { bg: 'var(--joy-palette-danger-softBg)', color: 'var(--joy-palette-danger-800)' };
            case UserOperationStatus.Processing:
            case UserOperationStatus.Waiting:
                return { bg: 'var(--joy-palette-warning-softBg)', color: 'var(--joy-palette-warning-800)' };
            default:
                return { bg: 'transparent', color: 'inherit' };
        }
    }

    const gameNameById = (id?: number) => {
        if (!id && id !== 0) return '-';
        const g = games.find(x => x.id === id);
        return g ? g.name : '-';
    }

    const getUserOperationTypeToPaymentOperationType = (type: UserOperationType): PaymentOperationType | null => {
        switch (type) {
            case UserOperationType.Replenishment:
                return PaymentOperationType.Deposit;
            case UserOperationType.Withdrawal:
                return PaymentOperationType.Withdraw;
            default:
                return null;
        }
    }

    const handleInfoClick = async (operation: UserOperationInfo) => {
        if (!operation.refId) {
            NotificationService.alertError("Нет данных для отображения");
            return;
        }

        const paymentOperationType = getUserOperationTypeToPaymentOperationType(operation.type);
        if (!paymentOperationType) {
            NotificationService.alertError("Неверный тип операции");
            return;
        }

        setSelectedOperation(operation);
        setWebhookModalOpen(true);
        setWebhookLoading(true);
        setWebhookData(null);

        try {
            const result = await getWebhookDataApi(operation.refId, paymentOperationType);
            if (result.result) {
                setWebhookData(result.value ?? null);
            } else {
                NotificationService.alertError("Ошибка при загрузке данных");
                setWebhookModalOpen(false);
            }
        } catch (error) {
            console.error(error);
            NotificationService.alertError("Ошибка при загрузке данных");
            setWebhookModalOpen(false);
        } finally {
            setWebhookLoading(false);
        }
    }

    const handleManualClick = (operation: UserOperationInfo) => {
        setManualOperation(operation);
        setManualPaymentId(operation.paymentId || '');
        setManualModalOpen(true);
    }

    const handleManualStart = async () => {
        if (!manualOperation) return;

        const trimmedPaymentId = manualPaymentId.trim();
        if (!trimmedPaymentId) {
            NotificationService.alertError("Укажите идентификатор операции из платежной системы");
            return;
        }

        try {
            setManualLoading(true);
            const result = await manualHandlePaymentApi(trimmedPaymentId, Number(manualOperation.id));
            if (result.result) {
                NotificationService.alertSuccess("Ручная проверка статуса запущена");
                setManualModalOpen(false);
                if (id && BigInt(id) > 0) {
                    loadData(BigInt(id)).catch(console.error);
                }
            } else {
                NotificationService.alertError("Ошибка при запуске ручной проверки");
            }
        } catch (error) {
            console.error(error);
            NotificationService.alertError("Ошибка при запуске ручной проверки");
        } finally {
            setManualLoading(false);
        }
    }

    return (
        <React.Fragment>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography level="h4">Операции пользователя</Typography>
            </Box>

            <Box
                className="SearchAndFilters-tabletUp"
                sx={{
                    borderRadius: 'sm',
                    py: 2,
                    display: {xs: 'none', sm: 'flex'},
                    flexWrap: 'wrap',
                    gap: 1.5,
                    '& > *': {
                        minWidth: {xs: '120px', md: '160px'},
                    },
                }}
            >
                <FormControl size="sm">
                    <FormLabel>Тип операции</FormLabel>
                    <Select size="sm" multiple placeholder="Все"
                            value={filterTypes}
                            onChange={(_, value) => setFilterTypes(value as number[])}>
                        <Option value={UserOperationType.Purchase}>Покупка</Option>
                        <Option value={UserOperationType.Sale}>Продажа</Option>
                        <Option value={UserOperationType.Withdrawal}>Вывод</Option>
                        <Option value={UserOperationType.Replenishment}>Пополнение</Option>
                        <Option value={UserOperationType.PromoBonus}>Бонус по промокоду</Option>
                    </Select>
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Статус</FormLabel>
                    <Select size="sm" multiple placeholder="Все"
                            value={filterStatuses}
                            onChange={(_, value) => setFilterStatuses(value as number[])}>
                        <Option value={UserOperationStatus.Success}>Успешно</Option>
                        <Option value={UserOperationStatus.Failure}>Не успешно</Option>
                        <Option value={UserOperationStatus.PenaltyPoint}>Штрафной балл</Option>
                        <Option value={UserOperationStatus.Refund}>Возврат</Option>
                        <Option value={UserOperationStatus.Waiting}>Ожидание</Option>
                        <Option value={UserOperationStatus.Processing}>Обработка</Option>
                        <Option value={UserOperationStatus.System}>Система</Option>
                    </Select>
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Игра</FormLabel>
                    <Select size="sm" placeholder="Все"
                            value={selectedGame}
                            onChange={(_, value) => setSelectedGame(value ?? -1)}>
                        <Option value={-1}>Все</Option>
                        {games.map(g => (
                            <Option key={g.id} value={g.id}>{g.name}</Option>
                        ))}
                    </Select>
                </FormControl>
                <FormControl sx={{flex: 1}} size="sm">
                    <FormLabel>Поиск</FormLabel>
                    <Input size="sm" placeholder="Поиск"
                           startDecorator={<SearchIcon/>}
                           value={search}
                           onChange={(e) => setSearch(e.target.value)} />
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>С даты</FormLabel>
                    <Input type="date" value={dateFrom ?? ''}
                           onChange={(e) => setDateFrom(e.target.value || undefined)} />
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>По дату</FormLabel>
                    <Input type="date" value={dateTo ?? ''}
                           onChange={(e) => setDateTo(e.target.value || undefined)} />
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'end' }}>
                    <Button size="sm" variant="solid" onClick={applyFilters}>Применить</Button>
                </Box>
            </Box>

            <Sheet
                className="SearchAndFilters-mobile"
                sx={{display: {xs: 'flex', sm: 'none'}, my: 1, gap: 1}}
            >
                <DebounceInput
                    size="sm"
                    placeholder="Поиск"
                    startDecorator={<SearchIcon/>}
                    sx={{flexGrow: 1}}
                    debounceTimeout={600}
                    handleDebounce={(val) => setSearch(val)}
                />
                <IconButton
                    size="sm"
                    variant="outlined"
                    color="neutral"
                    onClick={() => setFilterOpen(true)}
                >
                    <FilterAltIcon/>
                </IconButton>
                <Modal open={filterOpen} onClose={() => setFilterOpen(false)}>
                    <ModalDialog aria-labelledby="filter-modal" layout="fullscreen">
                        <ModalClose/>
                        <Typography id="filter-modal" level="h2">
                            Фильтры
                        </Typography>
                        <Divider sx={{my: 2}}/>
                        <Sheet sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                            <FormControl size="sm">
                                <FormLabel>Тип операции</FormLabel>
                                <Select size="sm" multiple placeholder="Все"
                                        value={filterTypes}
                                        onChange={(_, value) => setFilterTypes(value as number[])}>
                                    <Option value={UserOperationType.Purchase}>Покупка</Option>
                                    <Option value={UserOperationType.Sale}>Продажа</Option>
                                    <Option value={UserOperationType.Withdrawal}>Вывод</Option>
                                    <Option value={UserOperationType.Replenishment}>Пополнение</Option>
                                    <Option value={UserOperationType.PromoBonus}>Бонус по промокоду</Option>
                                </Select>
                            </FormControl>
                            <FormControl size="sm">
                                <FormLabel>Статус</FormLabel>
                                <Select size="sm" multiple placeholder="Все"
                                        value={filterStatuses}
                                        onChange={(_, value) => setFilterStatuses(value as number[])}>
                                    <Option value={UserOperationStatus.Success}>Успешно</Option>
                                    <Option value={UserOperationStatus.Failure}>Не успешно</Option>
                                    <Option value={UserOperationStatus.PenaltyPoint}>Штрафной балл</Option>
                                    <Option value={UserOperationStatus.Refund}>Возврат</Option>
                                    <Option value={UserOperationStatus.Waiting}>Ожидание</Option>
                                    <Option value={UserOperationStatus.Processing}>Обработка</Option>
                                    <Option value={UserOperationStatus.System}>Система</Option>
                                </Select>
                            </FormControl>
                            <FormControl size="sm">
                                <FormLabel>Игра</FormLabel>
                                <Select size="sm" placeholder="Все"
                                        value={selectedGame}
                                        onChange={(_, value) => setSelectedGame(value ?? -1)}>
                                    <Option value={-1}>Все</Option>
                                    {games.map(g => (
                                        <Option key={g.id} value={g.id}>{g.name}</Option>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="sm">
                                <FormLabel>С даты</FormLabel>
                                <Input type="date" value={dateFrom ?? ''}
                                       onChange={(e) => setDateFrom(e.target.value || undefined)} />
                            </FormControl>
                            <FormControl size="sm">
                                <FormLabel>По дату</FormLabel>
                                <Input type="date" value={dateTo ?? ''}
                                       onChange={(e) => setDateTo(e.target.value || undefined)} />
                            </FormControl>
                            <Button color="primary" onClick={() => { setFilterOpen(false); applyFilters(); }}>
                                Применить
                            </Button>
                            <Button color="neutral" onClick={() => setFilterOpen(false)}>
                                Закрыть
                            </Button>
                        </Sheet>
                    </ModalDialog>
                </Modal>
            </Sheet>

            <Box sx={{ overflow: 'auto', borderRadius: 8, mt: 1, display: { xs: 'none', sm: 'block' } }}>
                <Table aria-label="user-operations" stickyHeader>
                    <thead>
                    <tr>
                        <th style={{ width: 120 }}>Дата</th>
                        <th style={{ width: 80 }}>Игра</th>
                        <th style={{ width: 120 }}>Тип</th>
                        <th style={{ width: 100 }}>Заказ №</th>
                        <th style={{ width: 140 }}>Статус</th>
                        <th style={{ width: 110 }}>Сумма</th>
                        <th style={{ width: 90 }}>Валюта</th>
                        <th style={{ width: 120 }}>Комиссия</th>
                        <th>Заголовок / Описание</th>
                        <th style={{ width: 80 }}>Действия</th>
                    </tr>
                    </thead>
                    <tbody>
                    {operations.map(op => {
                        const showInfoButton = (op.type === UserOperationType.Replenishment || op.type === UserOperationType.Withdrawal) && op.refId;
                        const canManualHandle =
                            ((op.type === UserOperationType.Replenishment &&
                                    (op.status === UserOperationStatus.Processing ||
                                        (UserOperationStatus as any).New === op.status)) ||
                                (op.type === UserOperationType.Withdrawal &&
                                    (op.status === UserOperationStatus.Processing ||
                                        op.status === UserOperationStatus.Waiting ||
                                        (UserOperationStatus as any).New === op.status)));
                        return (
                            <tr key={op.id}>
                                <td>{formatDateTime(op.date)}</td>
                                <td>{gameNameById(op.gameId)}</td>
                                <td>{typeName(op.type)}</td>
                            <td>{op.refId && (op.type === UserOperationType.Sale || op.type === UserOperationType.Purchase)
                            ? <Link href={`/app/orders/${op.refId}`} target="_blank" level="body-sm">
                                    {op.refId}
                                </Link>
                            :<></>}</td>
                                <td>
                                    <Box sx={{
                                        display: 'inline-block',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 6,
                                        bgcolor: statusColors(op.status).bg,
                                        color: statusColors(op.status).color,
                                    }}>
                                        {statusName(op.status)}
                                    </Box>
                                </td>
                                <td>{formatRubles(op.sum)}</td>
                                <td>{op.currency}</td>
                                <td>{op.commission ? formatRubles(op.commission) : '-'}</td>
                                <td>
                                    {op.titleRu}{op.description ? ` — ${op.description}` : ''}
                                    {op.source === PurchaseSource.ExternalMarket &&
                                        <Chip size="sm" variant="soft" color="primary" sx={{ml: 1}}>
                                            поставщик {op.externalMarketKey}
                                        </Chip>}
                                </td>
                                <td>
                                    {(showInfoButton || canManualHandle) && (
                                        <Stack direction="row" spacing={0.5}>
                                            {showInfoButton && (
                                                <Tooltip title="Данные платежной системы">
                                                    <IconButton
                                                        size="sm"
                                                        variant="outlined"
                                                        onClick={() => handleInfoClick(op)}
                                                    >
                                                        <InfoIcon/>
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {canManualHandle && (
                                                <Tooltip title="Запустить ручную проверку статуса платежа">
                                                    <IconButton
                                                        size="sm"
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={() => handleManualClick(op)}
                                                    >
                                                        <RocketLaunchIcon/>
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </Table>
            </Box>

            <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                {operations.map((op) => {
                    const showInfoButton = (op.type === UserOperationType.Replenishment || op.type === UserOperationType.Withdrawal) && op.refId;
                    const canManualHandle =
                        ((op.type === UserOperationType.Replenishment &&
                                (op.status === UserOperationStatus.Processing ||
                                    (UserOperationStatus as any).New === op.status)) ||
                            (op.type === UserOperationType.Withdrawal &&
                                (op.status === UserOperationStatus.Processing ||
                                    op.status === UserOperationStatus.Waiting ||
                                    (UserOperationStatus as any).New === op.status)));
                    return (
                        <List key={op.id} size="sm" sx={{'--ListItem-paddingX': 0}}>
                            <ListItem
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'start',
                                }}
                            >
                                <ListItemContent sx={{display: 'flex', gap: 1.5}}>
                                    <div>
                                        <Typography level="title-sm">
                                            {op.titleRu}
                                            {op.source === PurchaseSource.ExternalMarket &&
                                                <Chip size="sm" variant="soft" color="primary" sx={{ml: 1}}>
                                                    поставщик {op.externalMarketKey}
                                                </Chip>}
                                        </Typography>
                                        <Typography level="body-xs">
                                            {typeName(op.type)} •
                                            <Box component="span" sx={{
                                                display: 'inline-block',
                                                px: 0.5,
                                                mx: 0.5,
                                                borderRadius: 6,
                                                bgcolor: statusColors(op.status).bg,
                                                color: statusColors(op.status).color,
                                            }}>
                                                {statusName(op.status)}
                                            </Box>
                                            {gameNameById(op.gameId)}
                                        </Typography>
                                    {op.refId && (op.type === UserOperationType.Sale || op.type === UserOperationType.Purchase)
                                    ? <Link href={`/app/orders/${op.refId}`} target="_blank" level="body-sm">
                                            Заказ #{op.refId}
                                        </Link>
                                    :<></>}
                                        <Typography level="body-xs">{formatDateTime(op.date)}</Typography>
                                        <Typography level="body-xs">{formatRubles(op.sum)} {op.currency}{op.commission ? ` (комиссия ${formatRubles(op.commission)})` : ''}</Typography>
                                        {/* RefId скрыт */}
                                        {op.description && (
                                            <Typography level="body-xs">{op.description}</Typography>
                                        )}
                                    </div>
                                </ListItemContent>
                                {(showInfoButton || canManualHandle) && (
                                    <Stack direction="row" spacing={0.5}>
                                        {showInfoButton && (
                                            <Tooltip title="Данные платежной системы">
                                                <IconButton 
                                                    size="sm" 
                                                    variant="outlined"
                                                    onClick={() => handleInfoClick(op)}
                                                >
                                                    <InfoIcon/>
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {canManualHandle && (
                                            <Tooltip title="Запустить ручную проверку статуса платежа">
                                                <IconButton 
                                                    size="sm" 
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() => handleManualClick(op)}
                                                >
                                                    <RocketLaunchIcon/>
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                )}
                            </ListItem>
                        </List>
                    );
                })}
            </Box>

            <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 1 }}>
                <Button size="sm" variant="plain" startDecorator={<KeyboardArrowLeftIcon/>} disabled={page === 1}
                        onClick={() => setPage(page - 1)}>
                    Назад
                </Button>
                <Stack direction="row" spacing={1}>
                    {getPages(pageCount).map((p) => (
                        <Button size="sm" key={'p' + p}
                                variant={page === Number(p) ? 'solid' : 'plain'}
                                disabled={!Number(p)}
                                onClick={() => Number(p) && setPage(Number(p))}>{p}</Button>
                    ))}
                </Stack>
                <Button size="sm" variant="plain" endDecorator={<KeyboardArrowRightIcon/>}
                        disabled={page === pageCount}
                        onClick={() => setPage(page + 1)}>
                    Далее
                </Button>
            </Stack>

            <Modal open={webhookModalOpen} onClose={() => setWebhookModalOpen(false)}>
                <ModalDialog 
                    sx={{ 
                        minWidth: { xs: '100%', sm: 600 },
                        maxWidth: { xs: '100%', sm: '90vw' },
                        width: { xs: '100%', sm: 'auto' },
                        maxHeight: { xs: '100vh', sm: '90vh' },
                        m: { xs: 0, sm: 'auto' },
                        borderRadius: { xs: 0, sm: 'md' }
                    }}
                >
                    <ModalClose/>
                    <Typography level="title-lg">
                        Данные платежной системы
                    </Typography>
                    <Divider sx={{ my: 2 }}/>
                    {webhookLoading ? (
                        <Typography level="body-md">Загрузка...</Typography>
                    ) : webhookData === null ? (
                        <Typography level="body-md">Данных нет</Typography>
                    ) : (
                        <Box 
                            sx={{ 
                                overflow: 'auto',
                                maxHeight: '60vh',
                                bgcolor: 'background.level1',
                                p: 2,
                                borderRadius: 8,
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                            }}
                        >
                            {formatJsonData(webhookData)}
                        </Box>
                    )}
                </ModalDialog>
            </Modal>
            
            <Modal open={manualModalOpen} onClose={() => setManualModalOpen(false)}>
                <ModalDialog
                    sx={{
                        minWidth: { xs: '100%', sm: 400 },
                        maxWidth: { xs: '100%', sm: '90vw' },
                        width: { xs: '100%', sm: 'auto' },
                        m: { xs: 0, sm: 'auto' },
                        borderRadius: { xs: 0, sm: 'md' }
                    }}
                >
                    <ModalClose/>
                    <Typography level="title-lg">
                        Ручная проверка статуса платежа
                    </Typography>
                    <Divider sx={{ my: 2 }}/>
                    <FormControl>
                        <FormLabel>Идентификатор операции из платежной системы</FormLabel>
                        <Input
                            value={manualPaymentId}
                            onChange={(e) => setManualPaymentId(e.target.value)}
                            placeholder="Введите идентификатор операции"
                        />
                    </FormControl>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={() => setManualModalOpen(false)}
                            disabled={manualLoading}
                        >
                            Отмена
                        </Button>
                        <Button
                            variant="solid"
                            color="primary"
                            onClick={handleManualStart}
                            loading={manualLoading}
                        >
                            Запустить
                        </Button>
                    </Stack>
                </ModalDialog>
            </Modal>
        </React.Fragment>
    );
}


